import usePropertiesUpdatingAllowed from 'components/domain/property/usePropertiesUpdatingAllowed';
import FormWithProvider from 'components/forms/form/Form';
import RadioGroupField from 'components/forms/radioGroupField/RadioGroupField';
import RichTextEditorField from 'components/forms/richTextEditor/RichTextEditorField';
import { GetPropertyOwnerResponseTO } from 'models/Owner';
import { CollapseSectionStyled } from 'pages/property/common/Common.styles';
import PermissionsAwareFieldset from 'pages/property/common/PermissionsAwareFieldset';
import {
  getFormDefaultValues,
  propertySettingsOwnersTabSchema,
} from 'pages/property/owners/PropertySettingsOwnersTab.schema';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useCommunicationSettingsFormSubmit from './useCommunicationSettingsFormSubmit';
import CommunicationSettingsFooter from './CommunicationSettingsFooter';

interface CommunicationSettingsProps {
  propertyOwnersData: GetPropertyOwnerResponseTO | undefined;
}

const CommunicationSettings = ({
  propertyOwnersData,
}: CommunicationSettingsProps) => {
  const { t } = useTranslation();
  const { updateCommunicationSettings } = useCommunicationSettingsFormSubmit();
  const propertiesUpdatingAllowed = usePropertiesUpdatingAllowed();

  if (!propertyOwnersData) {
    return null;
  }

  return (
    <FormWithProvider
      defaultValues={getFormDefaultValues({ propertyOwnersData })}
      resolver={propertySettingsOwnersTabSchema()}
      onSubmit={(values) => updateCommunicationSettings(values)}
      noValidate
    >
      <PermissionsAwareFieldset>
        <CollapseSectionStyled
          data-testid="communication-settings"
          defaultExpanded
          label={t('pageProperty.owners.communicationSettings.title')}
          variant="card"
        >
          <Row>
            <RadioGroupField
              horizontal
              name="communicationSettings.collectAndRemitTaxes"
              label={t('pageProperty.owners.collectAndRemitTaxes')}
              options={[
                { value: 'NO', label: t('common.no') },
                { value: 'YES', label: t('common.yes') },
              ]}
              colSmInput={8}
              colSmLabel={4}
              rightTooltip={t(
                'pageProperty.owners.collectAndRemitTaxesTooltip',
              )}
            />
          </Row>

          <Row>
            <RadioGroupField
              horizontal
              name="communicationSettings.copyOwnerWhenBookingConfirmed"
              label={t('pageProperty.owners.copyOwnerWhenBookingConfirmed')}
              options={[
                { value: 'NO', label: t('common.no') },
                { value: 'YES', label: t('common.yes') },
              ]}
              colSmInput={8}
              colSmLabel={4}
              rightTooltip={t(
                'pageProperty.owners.copyOwnerWhenBookingConfirmedTooltip',
              )}
            />
          </Row>

          <Row>
            <RichTextEditorField
              colSmInput={8}
              colSmLabel={4}
              label={t('pageProperty.owners.communicationSettings.notes')}
              name="communicationSettings.notes"
              readOnly={!propertiesUpdatingAllowed}
              rightTooltip={t(
                'pageProperty.owners.communicationSettings.notesTooltip',
              )}
            />
          </Row>

          <CommunicationSettingsFooter />
        </CollapseSectionStyled>
      </PermissionsAwareFieldset>
    </FormWithProvider>
  );
};

export default CommunicationSettings;
